// frontend_react/src/pages/restaurant/Restaurant.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../styles/restaurant.css";
import OrderScreen from "./OrderScreen";
import FloorPlan from "./FloorPlan";

import {
  fetchRooms,
  fetchCategories,
  fetchOrders,
  createOrder,
  closeOrder,
  mergeOrders,
  checkoutOrder,
  prepareOrderItems,
  addOrderItem,
  updateOrderItem,
  deleteOrderItem,
} from "../../services/restaurantApi";
import { useAdminScope } from "../../context/AdminScopeContext";

function Restaurant() {
  const [screen, setScreen] = useState("floor_plan");
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedCompanyUuid } = useAdminScope();

  const [allRooms, setAllRooms] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [orders, setOrders] = useState({});

  const numToUuid = useRef({});
  const uuidToNum = useRef({});
  const prodMap = useRef({});

  const companyScope = selectedCompanyUuid
    ? { companyUuid: selectedCompanyUuid }
    : {};

  const parseBody = (body) => {
    if (!body) return null;
    if (typeof body === "string") return JSON.parse(body);
    return body;
  };

  const apiFetch = useCallback(
    async (url, opts = {}) => {
      const method = (opts.method || "GET").toUpperCase();
      const body = parseBody(opts.body);

      if (url === "/rooms") {
        return (await fetchRooms(companyScope)).data;
      }

      if (url === "/categories") {
        return (await fetchCategories(companyScope)).data;
      }

      if (url === "/orders") {
        if (method === "POST") {
          return (await createOrder(body, companyScope)).data;
        }

        return (await fetchOrders(companyScope)).data;
      }

      if (url === "/orders/merge" && method === "POST") {
        return (await mergeOrders(body, companyScope)).data;
      }

      if (url.endsWith("/checkout") && method === "POST") {
        const segments = url.split("/");
        return (await checkoutOrder(segments[2], companyScope)).data;
      }

      if (url.endsWith("/close") && method === "POST") {
        const segments = url.split("/");
        return (await closeOrder(segments[2], companyScope)).data;
      }

      if (url.endsWith("/prepare") && method === "POST") {
        const segments = url.split("/");
        return (await prepareOrderItems(segments[2], companyScope)).data;
      }

      if (url.endsWith("/items") && method === "POST") {
        const segments = url.split("/");
        return (await addOrderItem(segments[2], body, companyScope)).data;
      }

      if (url.includes("/items/") && method === "PUT") {
        const segments = url.split("/");
        return (await updateOrderItem(segments[2], segments[4], body, companyScope)).data;
      }

      if (url.includes("/items/") && method === "DELETE") {
        const segments = url.split("/");
        return (await deleteOrderItem(segments[2], segments[4], companyScope)).data;
      }

      throw new Error(`Route restaurant non mappée : ${url}`);
    },
    [companyScope]
  );

  const transformOrders = useCallback((backendOrders = []) => {
    const result = {};

    for (const o of backendOrders) {
      const num = uuidToNum.current[o.table_uuid];
      if (num === undefined) continue;

      const cart = [];
      const preparedCart = [];

      for (const oi of o.order_items || []) {
        const p = prodMap.current[oi.menu_item_uuid] || {};
        const item = {
          ...p,
          uuid: oi.menu_item_uuid,
          item_backend_uuid: oi.uuid,
          quantite: oi.quantity,
          price: Math.round(oi.price * 100),
          filled_fields: oi.filled_fields,
          item_reference: oi.uuid,
        };

        if (oi.ready) preparedCart.push(item);
        else cart.push(item);
      }

      result[num] = {
        uuid: o.uuid,
        cart,
        preparedCart,
        couverts: o.couverts,
        createdAt: o.created_at ? new Date(o.created_at).getTime() : Date.now(),
        linkedWith: o.linked_with || [],
      };
    }

    return result;
  }, []);

  useEffect(() => {
    setScreen("floor_plan");
    setSelectedTable(null);
    setAllRooms([]);
    setAllCategories([]);
    setOrders({});
    numToUuid.current = {};
    uuidToNum.current = {};
    prodMap.current = {};
    setLoading(true);

    let cancelled = false;

    async function loadRestaurantData() {
      try {
        const [roomsRes, catsRes, ordersRes] = await Promise.all([
          apiFetch("/rooms"),
          apiFetch("/categories"),
          apiFetch("/orders"),
        ]);

        if (cancelled) return;

        setAllRooms(roomsRes.rooms || []);

        for (const room of roomsRes.rooms || []) {
          for (const t of room.tables || []) {
            numToUuid.current[t.number] = t.uuid;
            uuidToNum.current[t.uuid] = t.number;
          }
        }

        setAllCategories(catsRes.categories || []);

        for (const cat of catsRes.categories || []) {
          for (const item of cat.items || []) {
            prodMap.current[item.uuid] = {
              uuid: item.uuid,
              name: item.name,
              price: Math.round(item.price_ht * (1 + item.vat_rate / 100) * 100),
              category_id: cat.uuid,
              category_name: cat.name,
              production_area: item.production_area,
              fields: (item.fields || []).map((f) => ({
                field_name: f.field_name,
                field_type: f.field_type,
                category: f.category,
                options: f.options,
                default: f.default_value,
                price: f.price || 0,
              })),
            };
          }
        }

        setOrders(transformOrders(ordersRes.orders || []));
      } catch (err) {
        console.error("Chargement échoué:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRestaurantData();

    return () => {
      cancelled = true;
    };
  }, [apiFetch, transformOrders]);

  const refreshOrders = useCallback(async () => {
    try {
      const res = await apiFetch("/orders");
      setOrders(transformOrders(res.orders || []));
    } catch (err) {
      console.error("Refresh échoué:", err);
    }
  }, [apiFetch, transformOrders]);

  const ouvrirTable = async (tableNumber, couverts) => {
    if (!orders[tableNumber]) {
      const tableUuid = numToUuid.current[tableNumber];

      if (tableUuid) {
        try {
          await apiFetch("/orders", {
            method: "POST",
            body: { table_uuid: tableUuid, couverts: couverts || 1 },
          });
          await refreshOrders();
        } catch (err) {
          console.error("Erreur ouverture:", err);
        }
      }
    }

    setSelectedTable(tableNumber);
    setScreen("order");
  };

  const fermerTable = async (tableNumber) => {
    const order = orders[tableNumber];

    if (order?.uuid) {
      try {
        await apiFetch(`/orders/${order.uuid}/close`, { method: "POST" });
      } catch (err) {
        console.error(err);
      }
    }

    await refreshOrders();
    setScreen("floor_plan");
  };

  const fusionnerTables = async (tableNumbers) => {
    const uuids = tableNumbers.map((n) => numToUuid.current[n]).filter(Boolean);
    if (uuids.length < 2) return;

    try {
      await apiFetch("/orders/merge", {
        method: "POST",
        body: { table_uuids: uuids },
      });
      await refreshOrders();
    } catch (err) {
      console.error("Erreur fusion:", err);
    }
  };

  if (loading) {
    return (
      <div className="floor-plan">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <>
      {screen === "order" && orders[selectedTable] && (
        <OrderScreen
          setScreen={setScreen}
          tableNumber={selectedTable}
          order={orders[selectedTable]}
          setOrders={setOrders}
          fermerTable={fermerTable}
          refreshOrders={refreshOrders}
          apiFetch={apiFetch}
          productMap={prodMap.current}
          allCategories={allCategories}
        />
      )}

      {screen === "floor_plan" && (
        <FloorPlan
          setScreen={setScreen}
          ouvrirTable={ouvrirTable}
          fermerTable={fermerTable}
          orders={orders}
          allRooms={allRooms}
          fusionnerTables={fusionnerTables}
        />
      )}
    </>
  );
}

export default Restaurant;